import React from 'react';
import { useTranslation } from 'react-i18next';
import { localizePath } from '../../seo/site';
import { motion } from 'framer-motion';

const Relocations = () => {
  const { t, i18n } = useTranslation();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const movingServices = [
    {
      icon: (
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
          <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" strokeWidth="1.5" />
        </svg>
      ),
      title: t('services.relocations.internationalTitle'),
      description: t('services.relocations.internationalDesc')
    },
    {
      icon: (
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3m10-11v11a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: t('services.relocations.flatsHousesTitle'),
      description: t('services.relocations.flatsHousesDesc')
    },
    {
      icon: (
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="4" y="4" width="10" height="7" rx="1" strokeWidth="1.5" />
          <path d="M7 11v3h4v-3M5 14h8M17 6h3v10h-3zM17 10h3M17 13h3" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ),
      title: t('services.relocations.companiesOfficesTitle'),
      description: t('services.relocations.companiesOfficesDesc')
    },
    {
      icon: (
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="5" y="5" width="14" height="14" rx="2" strokeWidth="1.5" />
          <circle cx="9" cy="12" r="1.5" strokeWidth="1.5" />
          <path d="M14 8v8M16 8v8M5 12h4" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ),
      title: t('services.relocations.heavyLoadsTitle'),
      description: t('services.relocations.heavyLoadsDesc')
    },
    {
      icon: (
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M4 9h16M4 9v6a2 2 0 002 2h12a2 2 0 002-2V9M4 9V6a2 2 0 012-2h12a2 2 0 012 2v3M8 17v2M16 17v2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: t('services.relocations.furnitureTitle'),
      description: t('services.relocations.furnitureDesc')
    },
    {
      icon: (
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M15 7a4 4 0 100 8 4 4 0 000-8zm0 8l-7 7H4v-4l7-7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="15" cy="11" r="1" fill="currentColor" />
        </svg>
      ),
      title: t('services.relocations.otherTitle'),
      description: t('services.relocations.otherDesc')
    }
  ];

  const handleCtaClick = () => {
    const formElement = document.getElementById('request-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = localizePath('/#request-form', i18n.language);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white section-padding">
        <div className="container-custom">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('services.relocations.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200">
              {t('services.relocations.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('services.relocations.introHeading')}
            </h2>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              {t('services.relocations.introDescription')}
            </p>
          </motion.div>

          {/* Grid Section */}
          <motion.div
            variants={containerVariants}
            initial={false}
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {movingServices.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                className="bg-white rounded-2xl shadow-md p-8 border border-gray-100 hover:shadow-xl hover:border-teal-500 transition-shadow transition-colors duration-300 flex flex-col items-center text-center"
              >
                <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Detailed Content & Image Section */}
      <section className="section-padding bg-gray-50 border-t border-b border-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl bg-white"
            >
              <img
                src="/images/relocations.webp"
                width={1024} height={1024} decoding="async"
                alt={t('services.relocations.imageAlt')}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h3 className="text-3xl font-bold text-gray-900">
                {t('services.relocations.title')}
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('services.relocations.subtitle')}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-6 h-6 text-orange-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">{t('services.relocations.features.doorToDoor.title')}</h4>
                    <p className="text-gray-600">{t('services.relocations.features.doorToDoor.desc')}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-6 h-6 text-orange-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">{t('services.relocations.features.insured.title')}</h4>
                    <p className="text-gray-600">{t('services.relocations.features.insured.desc')}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-6 h-6 text-orange-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">{t('services.relocations.features.packing.title')}</h4>
                    <p className="text-gray-600">{t('services.relocations.features.packing.desc')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-orange-accent text-white">
        <div className="container-custom">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('services.ctaTitle')}
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t('services.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleCtaClick}
                className="btn-secondary text-lg px-8 py-4"
              >
                {t('common.getQuote')}
              </button>
              <a
                href="tel:+48577083279"
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-medium py-4 px-8 rounded-lg transition-all duration-300 text-lg flex items-center justify-center"
              >
                {t('common.contactUs')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Relocations;
